import {
  CloseIcon,
  IconButton,
  Text,
  type DataTableSource,
  type TableRow,
} from "@carbon6/c6-frontend-design";
import { useSyncExternalStore, type ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import styles from "./BulkActionBar.module.css";

export type BulkActionBarProps<Row extends TableRow> = {
  // 선택 상태의 단일 소유자. 바는 snapshot.selectedIds 를 읽고, 해제도 source 로 실행한다.
  source: DataTableSource<Row>;
  // 선택 액션 버튼들. 항상 표시해 자리를 유지한다. 활성·권한 규칙은 페이지가 소유한다.
  // 선택 액션이 없는 표면(북마크·보관 탭)은 생략한다.
  actions?: ReactNode;
  // 선택이 없을 때 액션 바로 오른쪽에 표시하는 정보(검색 결과 문구 등). 선택이 생기면
  // 개수 문구가 같은 자리를 쓴다.
  idleContent?: ReactNode;
  // 우측 슬롯(필터 컨트롤 등). 선택 여부와 관계없이 항상 표시한다. 선택 중에도 조건을
  // 바꿔야 하는 화면(정책 카탈로그의 스코프·워크스페이스 등)이 쓴다.
  rightContent?: ReactNode;
  // 개수 문구. 영어 UI 패널이 다른 문구로 바꾼다.
  countLabel?: (count: number) => string;
  // 해제(✕)의 접근성 이름. 영어 UI 패널이 다른 문구로 바꾼다.
  clearLabel?: string;
  ariaLabel: string;
};

/**
 * 체크박스 데이터 테이블/그리드 상단에 항상 표시하는 도구 스트립.
 *
 * 표 래퍼에 styles.host 를 합성하고, 스트립을 그 첫 행으로 렌더한다.
 * - idle: 살짝 들어간(recessed) 면으로 영역을 구분하고 idleContent 를 표시한다.
 * - 선택 시: 면은 그대로 둔다. 개수·해제(✕)를 표시하고 활성 액션 버튼만 accent 톤으로 바꾼다.
 * 스트립은 항상 렌더한다. 그래서 상태가 바뀌어도 레이아웃 시프트가 없다. 스트립은 표 헤더를
 * 덮지 않는다. 그래서 정렬·전체선택은 그대로 헤더가 소유한다.
 *
 * 순서는 [액션][개수 또는 idle 정보][우측 슬롯][해제] 다. 액션은 항상 같은 자리에 있다.
 * 결과 수와 선택 개수는 액션 바로 오른쪽 자리를 번갈아 쓴다(rules.md §17). 액션이 없는
 * 표면에서는 정보가 맨 앞에 있다.
 *
 * 소유: 스트립 배치와 상태 전환, 개수 문구, 해제 실행.
 * 비소유: 액션 목록과 그 규칙(페이지가 주입), 선택 상태(DataTableSource),
 * idle 정보·우측 슬롯의 내용.
 */
export function BulkActionBar<Row extends TableRow>({
  source,
  actions,
  idleContent,
  rightContent,
  countLabel = (count) => `${count}개 선택됨`,
  clearLabel = "선택 해제",
  ariaLabel,
}: BulkActionBarProps<Row>): ReactNode {
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const count = snapshot.selectedIds.size;
  const hasSelection = count > 0;

  // 표시할 것이 없는 표면(액션·idle 정보·우측 슬롯 없음)에는 빈 띠를 남기지 않는다.
  if (actions == null && idleContent == null && rightContent == null && !hasSelection) {
    return null;
  }

  return (
    <div
      className={hasSelection ? `${styles.bar} ${styles.barAccent}` : styles.bar}
      role="group"
      aria-label={ariaLabel}
    >
      {actions != null ? <div className={styles.actions}>{actions}</div> : null}
      {hasSelection ? (
        <Text variant={hatcheryTextModes["body-bold"]} className={styles.count}>
          {countLabel(count)}
        </Text>
      ) : idleContent != null ? (
        <div className={styles.idleContent}>{idleContent}</div>
      ) : null}
      {rightContent != null ? <div className={styles.rightContent}>{rightContent}</div> : null}
      {hasSelection ? (
        <IconButton
          aria-label={clearLabel}
          title={clearLabel}
          variant="ghost"
          size="sm"
          border={false}
          className={styles.clear}
          onClick={() => source.clearSelection()}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </div>
  );
}
